import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DelayedRender, SPLASH_DELAY_MS } from "./components/DelayedRender";

export type StartupStage = "bundle" | "config" | "auth";

interface StartupSplashControllerValue {
  readonly isStartupPending: boolean;
  readonly progressPercent: number;
  startStage: (stage: StartupStage, initialProgress?: number) => void;
  setStageProgress: (stage: StartupStage, progress: number) => void;
  completeStage: (stage: StartupStage) => void;
  finishStartup: () => void;
}

const progressMilestones: Record<StartupStage, number> = {
  bundle: 0.7,
  config: 0.85,
  auth: 1,
};
const progressTickMs = 120;
const progressEasingRate = 0.12;
const minProgressStep = 0.001;

type CompletedStages = Record<StartupStage, boolean>;
const initialCompletedStages: CompletedStages = {
  bundle: false,
  config: false,
  auth: false,
};

const StartupSplashControllerContext =
  createContext<StartupSplashControllerValue | null>(null);

function getProgressBounds(completedStages: CompletedStages) {
  if (completedStages.auth) {
    return { floor: 1, target: 1, isDone: true };
  }
  if (completedStages.config) {
    return {
      floor: progressMilestones.config,
      target: progressMilestones.auth,
      isDone: false,
    };
  }
  if (completedStages.bundle) {
    return {
      floor: progressMilestones.bundle,
      target: progressMilestones.config,
      isDone: false,
    };
  }
  return {
    floor: 0,
    target: progressMilestones.bundle,
    isDone: false,
  };
}

export function StartupSplashProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [isStartupPending, setIsStartupPending] = useState(true);
  const [completedStages, setCompletedStages] = useState<CompletedStages>(
    initialCompletedStages,
  );
  const [displayProgress, setDisplayProgress] = useState(0);

  const startStage = useCallback(
    (_stage: StartupStage, _initialStageProgress = 0) => {},
    [],
  );

  const setStageProgress = useCallback(
    (_stage: StartupStage, _progress: number) => {},
    [],
  );

  const completeStage = useCallback((stage: StartupStage) => {
    setCompletedStages((previous) => ({ ...previous, [stage]: true }));
  }, []);

  const finishStartup = useCallback(() => {
    setCompletedStages({
      bundle: true,
      config: true,
      auth: true,
    });
    setDisplayProgress(1);
    setIsStartupPending(false);
  }, []);

  useEffect(() => {
    const bounds = getProgressBounds(completedStages);
    setDisplayProgress((previous) => Math.max(previous, bounds.floor));
  }, [completedStages]);

  useEffect(() => {
    if (!isStartupPending || completedStages.auth) {
      return;
    }

    const intervalId = setInterval(() => {
      setDisplayProgress((previous) => {
        const bounds = getProgressBounds(completedStages);
        const normalized = Math.max(previous, bounds.floor);
        const remaining = bounds.target - normalized;
        if (remaining <= minProgressStep) {
          return bounds.target;
        }
        const step = Math.max(remaining * progressEasingRate, minProgressStep);
        return Math.min(bounds.target, normalized + step);
      });
    }, progressTickMs);

    return () => clearInterval(intervalId);
  }, [isStartupPending, completedStages]);

  useEffect(() => {
    if (!completedStages.auth) {
      return;
    }
    setDisplayProgress(1);
    setIsStartupPending(false);
  }, [completedStages.auth]);

  const progressPercent = useMemo(() => {
    return Math.round(displayProgress * 100);
  }, [displayProgress]);

  const value = useMemo<StartupSplashControllerValue>(
    () => ({
      isStartupPending,
      progressPercent,
      startStage,
      setStageProgress,
      completeStage,
      finishStartup,
    }),
    [
      isStartupPending,
      progressPercent,
      startStage,
      setStageProgress,
      completeStage,
      finishStartup,
    ],
  );

  return (
    <StartupSplashControllerContext.Provider value={value}>
      {children}
    </StartupSplashControllerContext.Provider>
  );
}

export function useStartupSplashController() {
  return useContext(StartupSplashControllerContext);
}

export function useStartupSplashProgress() {
  return useStartupSplashController()?.progressPercent;
}

export function StartupStageReporter({
  stage,
  pendingProgress = 0.35,
}: Readonly<{
  stage: StartupStage;
  pendingProgress?: number;
}>) {
  const controller = useStartupSplashController();
  const startStage = controller?.startStage;
  const completeStage = controller?.completeStage;

  useEffect(() => {
    startStage?.(stage, pendingProgress);
    return () => completeStage?.(stage);
  }, [startStage, completeStage, stage, pendingProgress]);

  return null;
}

export function StartupSplashHost({
  splashScreen,
  delayMs = SPLASH_DELAY_MS,
}: Readonly<{
  splashScreen: ReactNode;
  delayMs?: number;
}>) {
  const controller = useStartupSplashController();

  if (!controller?.isStartupPending) {
    return null;
  }

  return <DelayedRender delayMs={delayMs} fallback={splashScreen} />;
}
